import { Component, Injector, OnDestroy, ViewChild } from '@angular/core';
import { FormComponent } from 'src/app/core/abstracts/components/form.component';
import { UntypedFormArray } from '@angular/forms';
import { TabEnum } from 'src/app/core/enums/tab.enum';
import { KindComponent } from '../kind/kind.component';
import { RegexPatternValidator } from 'src/app/core/validators/regex-pattern.validator';
import { Einzelabfrage } from 'src/app/core/models/einzelabfrage';
import { HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';
import { OdbsDateValidator } from 'src/app/core/validators/odbs-date.validator';
import { Kind } from 'src/app/core/models/kind';
import { PiwikService } from 'src/app/shared/services/piwik';
import { IPxErrorBoxComponent } from 'src/app/shared/library/px-ui/px-forms/components';
import { StaticCommonValidator } from 'src/app/shared/library/px-ui/px-forms/validators';
import { DateUtil, ObjectUtil } from 'src/app/shared/library/px-ui/px-forms/utils';
import {kgNrValidator} from "../../core/validators/validators";

@Component({
    selector: 'odbs-einzelabfrage',
    templateUrl: './einzelabfrage.component.html',
    styleUrls: ['./einzelabfrage.component.scss'],
})
export class EinzelabfrageComponent extends FormComponent implements OnDestroy {
    private subSink = new Subscription();
    gesendet = false;
    @ViewChild('fehlerboxComponent') fehlerbox: IPxErrorBoxComponent;

    constructor(private injector: Injector, private matomoService: PiwikService) {
        super(injector);
        this.tabService.setCurrentTab(TabEnum.EINZELABFRAGE);
        this.init();
    }

    init() {
        this.formGroup = this.f.group(this.newEinzelabfrage());
        this.formGroup.addControl('kinder', new UntypedFormArray([KindComponent.newKindFormGroup()]));
        this.subSink.add(
            this.formGroup
                .get('zeitraumVon')
                .valueChanges.subscribe((value) => this.formGroup.get('zeitraumBis').updateValueAndValidity())
        );
        this.gesendet = false;
    }

    private newEinzelabfrage() {
        return {
            kindergeldnummer: ['', [StaticCommonValidator.required, kgNrValidator]],
            ordnungkriterium: [
                '',
                {
                    validators: [StaticCommonValidator.required, RegexPatternValidator.ordnungkriterium],
                    updateOn: 'blur',
                },
            ],
            zeitraumVon: [
                '01.' + DateUtil.today('yyyy'),
                [
                    StaticCommonValidator.required,
                    OdbsDateValidator.validJahrMonat,
                    OdbsDateValidator.customDateInFuture,
                    RegexPatternValidator.abfragezeitraum,
                ],
            ],
            zeitraumBis: [
                DateUtil.today('MM.yyyy'),
                [
                    OdbsDateValidator.validJahrMonat,
                    RegexPatternValidator.abfragezeitraum,
                    OdbsDateValidator.customDateInFuture,
                    OdbsDateValidator.isVonBisDate(() => this.zeitraumVon),
                ],
            ],
        };
    }

    private resetEinzelabfrage() {
        this.kinder.clear();
        this.formGroup.reset({
            kindergeldnummer: '',
            ordnungkriterium: '',
            zeitraumVon: '01.' + DateUtil.today('yyyy'),
            zeitraumBis: DateUtil.today('MM.yyyy'),
        });
        this.kinder.push(KindComponent.newKindFormGroup());
    }

    get zeitraumVon() {
        return this.formGroup && this.formGroup.contains('zeitraumVon') && this.formGroup.get('zeitraumVon').value;
    }

    get kinder() {
        return this.formGroup.get('kinder') as UntypedFormArray;
    }

    absenden() {
        if (this.formGroup.valid) {
            const einzelabfrage = this.mapFormToJson();
            this.subSink = this.saveWithBody('/einzelabfrage', einzelabfrage).subscribe(
                (response) => {
                    this.storeService.setData(einzelabfrage, 'anfrage');
                    this.storeService.setData(response, 'kiwi');
                    this.gesendet = true;
                    this.matomoService.trackEvent('buttonKlicken', 'Einzelabfrage absenden');
                },
                (error: HttpErrorResponse) => {
                    this.routerService.goToErrorPage();
                    this.matomoService.trackEvent('buttonKlicken', 'Einzelabfrage fehlgeschlagen');
                }
            );
        } else {
            this.fehlerbox.validate(this.formGroup);
            this.formGroup.markAllAsTouched();
        }
    }

    neueAnfrage() {
        this.storeService.setData({}, 'anfrage');
        this.storeService.setData({}, 'kiwi');
        this.gesendet = false;
    }

    private mapFormToJson() {
        const einzelabfrage: Einzelabfrage = { ...this.formGroup.value };
        const zeitraumBis = ObjectUtil.isEmpty(einzelabfrage.zeitraumBis)
            ? DateUtil.today('MM.yyyy')
            : einzelabfrage.zeitraumBis;
        einzelabfrage.zeitraumBis = DateUtil.format(zeitraumBis, 'MM.yyyy', 'yyyy-MM');

        einzelabfrage.zeitraumVon = DateUtil.format(einzelabfrage.zeitraumVon, 'MM.yyyy', 'yyyy-MM');
        const kinder: Kind[] = [];
        einzelabfrage.kinder.forEach((kind) => {
            kinder.push({
                kindGeburtsdatum: DateUtil.toService(kind.kindGeburtsdatum),
                kindVorname: kind.kindVorname,
            });
        });
        einzelabfrage.kinder = kinder;
        return einzelabfrage;
    }

    formularLeeren() {
        this.resetEinzelabfrage();
        this.fehlerbox.reset();
    }

    ngOnDestroy(): void {
        this.subSink.unsubscribe();
    }
}
